'use client';
// Audits internes (design isAudits — ISO 15189 §8.8) : programme master-detail,
// grille de contrôle figée à la planification, constats → non-conformités,
// clôture bloquée tant qu'un écart n'est pas déclaré en NC. Sous-onglet
// « Modèles de grille » (édités dans ged-setting.audGrids).
import React, { useEffect, useMemo, useState } from 'react';
import { useOngletAdresse } from '@/lib/useAdresse';
import { LigneLien } from '@/shared/Tableau';
import { cheminDe } from '@/lib/adresse';
import { useGed, userName } from '@/lib/store';
import { AUDIT_STATES, CONSTAT_TYPES, NC_STATES, vocabMeta, fmtDate, fmtDateTime, joursRestants, estEnRetard, MOTIF_DEROGATION_MIN } from '@/lib/meta';
import { useRemonteEnHaut,
  TitrePage, I, card, inputStyle,
  Skeleton, EmptyState, FilterBar, MentionTri, LateBadge, FieldError, RequiredHint, ConfirmButton, useBusy, RefISO, Selecteur, BoutonCreer } from '@/components/ui';
import { BoutonJournal } from '@/components/BoutonJournal';
import { Liens } from '@/components/Liens';
import { useFormulaire } from '@/components/Formulaire';
import type { GedGrilleItem, GedSettings } from '@/lib/types';
import { OngletsDossier, PanneauDossier, BarreAction } from '@/components/Dossier';
import { AccesAudits } from '@/components/AccesAudits';
// La règle de clôture, écrite UNE fois pour la NC, l'audit et la réclamation.
import { BlocageCloture } from '@/components/Cloture';
import type { GedAudit } from '@/lib/types';
import { useTri, BoutonsTri } from '@/shared/Tri';
import { Norme } from '@/components/Norme';
// Le panneau « dire ce que le geste emporte », partagé avec les NC et les actions.
import { SuppressionRaisonnee } from '@/components/SuppressionRaisonnee';

import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
import { PiedActions } from '@/shared/PiedActions';
const lbl: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 5 };
const pill = (m: { label: string; bg: string; fg: string }) => (
  <span style={{ display: 'inline-flex', padding: '2px 9px', borderRadius: 'var(--radius-pill)', background: m.bg, color: m.fg, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{m.label}</span>
);

const AIDE_ECART = 'Écart : le point de contrôle n’est pas satisfait — il doit être déclaré en non-conformité avant que l’audit puisse être clôturé.';
const AIDE_NA = 'N/A : non applicable — le point de contrôle ne concerne pas le périmètre audité. Il n’entre ni dans les conformités ni dans les écarts.';

/**
 * Annuler un audit inscrit au programme par erreur.
 *
 * La conséquence propre à l'audit est nommée : il cesse d'être compté comme
 * « planifié non tenu ». C'est l'objet même de cet indicateur — le retirer du
 * programme et le laisser passer pour un manquement diraient deux choses
 * différentes de la même ligne.
 */
function AnnulerAudit({ a }: { a: GedAudit }) {
  const { smq } = useGed();
  const [ouvert, setOuvert] = useState(false);
  const [motif, setMotif] = useState('');

  if (!ouvert) {
    return (
      <Bouton taille="sm" variante="discret" onClick={() => setOuvert(true)}
        title="L’audit n’a pas lieu d’être : le retirer du programme, en le motivant"
        style={{ marginInlineStart: 'auto' }}>
        Planifié par erreur…
      </Bouton>
    );
  }

  return (
    <div style={{ flex: '1 1 100%' }}>
      <SuppressionRaisonnee
        titre="Annuler cet audit — planifié par erreur"
        objet={<><strong>{a.ref}</strong> — {a.intitule}</>}
        nature="retrait"
        consequences={[
          {
            cle: 'programme',
            texte: 'L’audit sort du programme et ne sera pas compté « planifié non tenu ».',
            gravite: 'attention' as const,
            detail: 'Il ne sera pas compté non plus comme réalisé : rien n’a été audité.',
          },
          {
            cle: 'trace',
            texte: 'La ligne, sa référence et son journal restent lisibles.',
            gravite: 'info' as const,
            detail: 'Rien n’est effacé — le motif ci-dessous s’inscrit au journal signé.',
          },
        ]}
        note={(
          <div style={{ display: 'grid', gap: 6 }}>
            <label style={lbl} htmlFor={`annul-${a.ref}`}>
              Motif de l’annulation <span style={{ color: 'var(--red-600)' }}>*</span>
            </label>
            <textarea id={`annul-${a.ref}`} value={motif} rows={2}
              onChange={(e) => setMotif(e.target.value)}
              placeholder="Ex. doublon du programme 2026 : le même périmètre est déjà couvert par AUD-2026-02."
              style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
            <span>{motif.trim().length}/{MOTIF_DEROGATION_MIN} caractères minimum — relu en audit.</span>
          </div>
        )}
        libelleAction="Annuler l’audit"
        onConfirmer={async () => {
          if (motif.trim().length < MOTIF_DEROGATION_MIN) {
            return `Motif trop court : ${MOTIF_DEROGATION_MIN} caractères au minimum.`;
          }
          const ok = await smq(`ged/audits/${a.documentId}/annuler`, { motif: motif.trim() },
            'Audit annulé — motif inscrit au journal.', `Annulation de ${a.ref}`);
          return ok ? '' : 'L’annulation n’a pas abouti.';
        }}
        onAnnuler={() => { setOuvert(false); setMotif(''); }} />
    </div>
  );
}

/** Un audit planifié dont la date est passée : sans marqueur, il se lisait comme
 *  celui de la semaine prochaine. */
export const auditEnRetard = (a: GedAudit) => a.statut === 'planifie' && estEnRetard(a.datePlanifiee);

/** Un audit non soldé (planifié ou réalisé, pas encore clôturé). Source unique
 *  — le tableau de bord s'en sert aussi, pour ne pas diverger du registre. */
export const auditOuvert = (a: GedAudit) => a.statut !== 'cloture' && a.statut !== 'annule';

/** Masqué plutôt que démonté : la saisie survit à un clic dans la liste. */
function PlanForm({ visible, onDone, onDirty }: { visible: boolean; onDone: () => void; onDirty: (d: boolean) => void }) {
  const { smq, settings } = useGed();
  const vide = { intitule: '', auditType: 'interne', perimetre: '', auditeur: '', datePlanifiee: '', grilleId: '' };
  const [f, setF] = useState(vide);
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const gedUsers = settings?.gedUsers || [];
  const grids = settings?.audGrids || [];

  const modifie = JSON.stringify(f) !== JSON.stringify(vide);
  useEffect(() => { onDirty(modifie); }, [modifie, onDirty]);

  /* Contrôle au socle partagé (14/08/2026). */
  const declaration = React.useMemo(() => [
    { id: 'aud-titre', label: 'Intitulé de l’audit', valeur: f.intitule, requis: true },
  ], [f.intitule]);
  const { ref: refFormulaire, verifier, Bandeau } = useFormulaire(declaration);

  const submit = () => run('creer', async () => {
    if (!verifier()) return;
    setErreur('');
    const ok = await smq('ged/audits', f, 'Audit ajouté au programme.');
    if (ok) { setF(vide); onDone(); }
  });

  return (
    <div ref={refFormulaire} style={{ ...card, padding: 20, display: visible ? 'block' : 'none' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 14px' }}>
        <h2 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, color: 'var(--text-strong)' }}>Planifier un audit</h2>
        <RequiredHint />
      </div>
      <Bandeau />
      <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={lbl} htmlFor="aud-titre">Intitulé <span style={{ color: 'var(--red-600)' }}>*</span></label>
          <input id="aud-titre" value={f.intitule} onChange={(e) => { setF({ ...f, intitule: e.target.value }); if (erreur) setErreur(''); }}
            aria-invalid={!!erreur} style={{ ...inputStyle, borderColor: erreur ? 'var(--red-600)' : undefined }} />
          <FieldError>{erreur}</FieldError>
        </div>
        <div><label style={lbl}>Type</label>
          <select value={f.auditType} onChange={(e) => setF({ ...f, auditType: e.target.value })} style={inputStyle}>
            <option value="interne">Interne</option><option value="blanc">Audit blanc</option><option value="externe">Externe</option>
          </select></div>
        <div><label style={lbl}>Périmètre</label><input value={f.perimetre} onChange={(e) => setF({ ...f, perimetre: e.target.value })} placeholder="Ex. Pré-analytique · Accueil" style={inputStyle} /></div>
        <div><label style={lbl}>Auditeur</label>
          <Selecteur value={f.auditeur} onChange={(v) => setF({ ...f, auditeur: v })} style={inputStyle}>
            <option value="">—</option>
            {gedUsers.map((u) => <option key={u.id} value={u.id}>{u.nom}</option>)}
          </Selecteur></div>
        <div><label style={lbl}>Date planifiée</label><input type="date" value={f.datePlanifiee} onChange={(e) => setF({ ...f, datePlanifiee: e.target.value })} style={inputStyle} /></div>
        <div><label style={lbl}>Grille de contrôle</label>
          <Selecteur value={f.grilleId} onChange={(v) => setF({ ...f, grilleId: v })} style={inputStyle}>
            <option value="">Sans grille</option>
            {grids.map((g) => <option key={g.id} value={g.id}>{g.nom} ({g.items.length} points)</option>)}
          </Selecteur>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 5 }}>La grille est figée à la planification : elle ne suivra pas les modifications ultérieures du modèle.</div>
        </div>
      </div>
      <PiedActions taille="sm" style={{ marginTop: 14 }} annuler={{ onClick: () => { setF(vide); setErreur(''); onDone(); } }} principale={{ libelle: 'Ajouter au programme', libelleChargement: 'Ajout…', chargement: estOccupe('creer'), disabled: estOccupe(), onClick: () => void submit() }} />
    </div>
  );
}

type OngletAudit = 'grille' | 'constats' | 'liens';
/* Les valeurs ADMISES dans l'adresse. Elle existe pour refuser
   `?onglet=nimportequoi` : sans elle, une adresse tronquée par un logiciel de
   messagerie ouvrirait un onglet vide au lieu du premier. */
const ONGLETS_ONGLETAUDIT: readonly OngletAudit[] = ['grille', 'constats', 'liens'];

/**
 * Le DOSSIER d'un audit — exporté sous le nom `AuditDossier`.
 *
 * Exporté parce que la fiche s'ouvre désormais aussi EN PANNEAU par-dessus
 * l'objet d'où l'on vient (une NC née d'un écart, par exemple), au lieu de
 * changer d'écran. Même raison qu'`ActionDossier` : recopier la fiche aurait
 * donné deux dossiers d'audit qui divergent.
 *
 * `enPanneau` ne change rien au contenu : il retire le cadre de carte, que le
 * panneau fournit déjà (sinon carte dans une carte, double bordure), et il
 * neutralise les liens qui feraient QUITTER l'écran — le panneau a son propre
 * bouton « Ouvrir en pleine page » dans son en-tête.
 */
export function AuditDossier({ a, enPanneau = false }: { a: GedAudit; enPanneau?: boolean }) {
  return <Detail a={a} enPanneau={enPanneau} />;
}

function Detail({ a, enPanneau = false }: { a: GedAudit; enPanneau?: boolean }) {
  const { smq, settings, session, events, loadJournal, go, ouvrirPanneau, ncs, liens } = useGed();
  const [ct, setCt] = useState({ type: 'ecart', description: '' });
  const [erreurCt, setErreurCt] = useState('');
  /* L'onglet vit dans l'ADRESSE (relevé P0.8) : il se partage par lien, F5 y
     revient, et « Précédent » ramène à l'onglet précédent au lieu de quitter
     l'écran. */
  const [onglet, setOnglet] = useOngletAdresse<OngletAudit>('grille', ONGLETS_ONGLETAUDIT);
  const { run, estOccupe } = useBusy();
  useEffect(() => { void loadJournal(a.ref); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [a.documentId, a.statut]);
  const isRQ = session.roles.includes('rq');
  const canWork = isRQ || a.auditeur === session.key || session.roles.includes('auditeur');
  const journal = events[a.ref] || [];
  const constats = a.constats || [];
  const grille = a.grille;
  /**
   * Les obstacles à la clôture, RECALCULÉS à chaque rendu — jamais stockés.
   *
   * Le contrôle s'arrêtait à mi-chemin : il exigeait qu'un écart soit DÉCLARÉ
   * en non-conformité, sans jamais aller voir ce qu'elle était devenue. Un
   * audit pouvait donc être clôturé en désignant dix NC dont aucune n'était
   * traitée. Le serveur applique désormais la même règle que pour la NC et la
   * réclamation (`backend/src/api/ged/services/cloture.ts`), et fait foi.
   */
  const ecarts = [
    ...(grille?.items || []).filter((it) => it.verdict === 'ecart'),
    ...constats.filter((c) => c.type === 'ecart'),
  ];
  const pendEcarts = ecarts.filter((e) => !e.ncRef).length;
  const ncsOuvertes = ecarts
    .map((e) => (e.ncRef ? ncs.find((n) => n.ref === e.ncRef) : undefined))
    .filter((n): n is NonNullable<typeof n> => !!n && n.statut !== 'cloturee');
  const blocagesCloture = [
    ...(pendEcarts > 0
      ? [{ ref: '', libelle: `${pendEcarts} écart(s) non déclaré(s) en non-conformité` }]
      : []),
    // Dédoublonné : deux points de grille peuvent renvoyer à la même NC, et
    // l'afficher deux fois ferait croire à deux obstacles distincts.
    ...Array.from(new Map(ncsOuvertes.map((n) => [n.ref, n])).values()).map((n) => ({
      ref: n.ref,
      libelle: NC_STATES[n.statut]?.label || n.statut,
      // En panneau, ouvrir la NC ferait sortir de la superposition : on nomme
      // l'obstacle sans promettre un voyage (cf. `ActionDossier`).
      onOuvrir: enPanneau ? undefined : () => gotoNC(n.ref),
    })),
  ];
  const nbLiens = liens.filter((l) => (l.sourceType === 'audit' && l.sourceRef === a.ref) || (l.cibleType === 'audit' && l.cibleRef === a.ref)).length;

  // La NC citée par un écart s'ouvre EN PANNEAU au-dessus de l'audit : on la
  // consulte pour savoir si elle bloque la clôture, puis on referme — la grille
  // en cours de remplissage n'a pas bougé (décision du 12/08/2026).
  const gotoNC = (ref: string) => {
    const nc = ncs.find((n) => n.ref === ref);
    if (nc) ouvrirPanneau({ type: 'nc', valeur: nc.documentId, origine: a.ref });
  };

  return (
    <div style={enPanneau
      ? { padding: '4px 2px', display: 'grid', gap: 12 }
      : { ...card, padding: 20, display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{a.ref}</span>
        <BoutonJournal reference={a.ref} quoi="de cet audit" evenements={journal} />
        {pill(vocabMeta(AUDIT_STATES, a.statut))}
        {auditEnRetard(a) && <LateBadge jours={joursRestants(a.datePlanifiee)} libelleRetard="Date dépassée" />}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 12.5, color: auditEnRetard(a) ? 'var(--red-600)' : 'var(--text-muted)', fontWeight: auditEnRetard(a) ? 600 : 400 }}>
          Planifié le {fmtDate(a.datePlanifiee)}{a.dateRealisee ? ` · réalisé le ${fmtDate(a.dateRealisee)}` : ''}
        </span>
      </div>
      <h2 style={{ margin: 0, fontSize: 16.5, fontWeight: 700, color: 'var(--text-strong)' }}>{a.intitule}</h2>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{a.perimetre || '—'} · Auditeur : {userName(settings, a.auditeur)}</div>
      <PersonnesAuditees a={a} peutEcrire={canWork && a.statut !== 'cloture'} />

      <OngletsDossier id="aud" actif={onglet} onChange={setOnglet} items={[
        { k: 'grille', label: 'Grille', n: grille?.items.length },
        { k: 'constats', label: 'Constats', n: constats.length },
        { k: 'liens', label: 'Liens', n: nbLiens },
      ]} />

      <PanneauDossier id="aud" actif={onglet}>

      {onglet === 'grille' && !grille && (
        <EmptyState titre="Cet audit a été planifié sans grille de contrôle."
          indice="La grille est figée à la planification : elle ne peut pas être ajoutée après coup. Les constats libres restent disponibles." />
      )}
      {onglet === 'grille' && grille && (
        <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
          {/* Lot 7 (11/08/2026) — le CARTOUCHE d'exécution : tout ce que la
              feuille d'audit papier portait en tête, servi d'un bloc. */}
          <div style={{ padding: '10px 14px', background: 'var(--gray-25)', borderBottom: '1px solid var(--border-default)', display: 'grid', gap: 3 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>
              Grille de contrôle — {grille.nom}
              {a.statut === 'planifie' && <span style={{ fontWeight: 500, color: 'var(--text-muted)' }}> · marquez l’audit « réalisé » pour remplir la grille</span>}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <span><strong>Audit :</strong> {a.ref} — {a.intitule}</span>
              <span><strong>Type :</strong> {a.auditType === 'interne' ? 'interne' : a.auditType}</span>
              <span><strong>Périmètre :</strong> {a.perimetre || '—'}</span>
              <span><strong>Auditeur :</strong> {userName(settings, a.auditeur)}</span>
              <span><strong>Planifié :</strong> {fmtDate(a.datePlanifiee)}{a.dateRealisee ? ` · réalisé le ${fmtDate(a.dateRealisee)}` : ''}</span>
              <span><strong>Avancement :</strong> {grille.items.filter((x) => x.verdict).length}/{grille.items.length} point(s) statués</span>
            </div>
          </div>
          {grille.items.map((it) => (
            <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderTop: '1px solid var(--border-subtle)', flexWrap: 'wrap' }}>
              <span style={{ flex: '1 1 260px', fontSize: 13, color: 'var(--text-body)' }}>
                {it.article && (
                  <span title="Article de l'ISO 15189:2022 que ce point éprouve."
                    style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, fontWeight: 700, color: 'var(--color-primary-active)', marginInlineEnd: 8, cursor: 'help' }}>
                    §{it.article}
                  </span>
                )}
                {it.label}
                {it.attendu && (
                  <span title={`Attendu : ${it.attendu}`} style={{ marginInlineStart: 7, cursor: 'help', color: 'var(--text-muted)' }}>
                    <I n="info" s={13} style={{ display: 'inline-block', verticalAlign: '-2px' }} />
                  </span>
                )}
                <AideAuditeur it={aide(settings, a, it)} />
              </span>
              {(['conforme', 'ecart', 'na'] as const).map((v) => {
                const on = it.verdict === v;
                const col = v === 'conforme' ? 'var(--green-600)' : v === 'ecart' ? 'var(--red-600)' : 'var(--text-muted)';
                const inactif = a.statut !== 'realise' || !canWork || estOccupe();
                return (
                  <button key={v} disabled={inactif}
                    title={v === 'ecart' ? AIDE_ECART : v === 'na' ? AIDE_NA : undefined}
                    onClick={() => void run(`g-${it.id}`, () => smq(`ged/audits/${a.documentId}/grille`, { itemId: it.id, verdict: on ? null : v }, on ? 'Verdict retiré.' : 'Verdict enregistré.'))}
                    style={{ fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '4px 11px', cursor: inactif ? 'default' : 'pointer', border: `1.5px solid ${on ? col : 'var(--border-default)'}`, background: on ? (v === 'conforme' ? 'var(--green-50)' : v === 'ecart' ? 'var(--red-50)' : 'var(--gray-50)') : 'var(--surface-card)', color: on ? col : 'var(--text-muted)', opacity: a.statut === 'realise' ? 1 : 0.6 }}>
                    {v === 'conforme' ? 'Conforme' : v === 'ecart' ? 'Écart' : 'N/A'}
                  </button>
                );
              })}
              {it.verdict === 'ecart' && it.ncRef && (
                enPanneau
                  ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: 'var(--color-primary-active)' }}>{it.ncRef}</span>
                  : <Bouton variante="primaire" taille="sm" onClick={() => gotoNC(it.ncRef!)} style={{ fontFamily: 'var(--font-mono)' }}>{it.ncRef} →</Bouton>
              )}
              {/*
                L'écart PROPOSE sa non-conformité, au lieu de l'attendre
                (12/08/2026, demande utilisatrice).

                Le bouton « Déclarer en NC » existait, mais aligné avec les
                trois pastilles de verdict : rien ne distinguait une action
                obligatoire d'un réglage. Or un écart NON déclaré bloque la
                clôture de l'audit — on ne le découvrait qu'au moment de
                clôturer, parfois des semaines plus tard, quand la visite et
                ses détails s'étaient effacés.

                La proposition s'affiche donc sous CHAQUE écart sans NC, dit ce
                qu'elle emporte, et se laisse remettre à plus tard : ce n'est
                pas un blocage de saisie, c'est un rappel posé au bon moment.
              */}
              {it.verdict === 'ecart' && !it.ncRef && isRQ && (
                <div style={{ flexBasis: '100%', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', margin: '2px 0 2px', padding: '8px 11px', borderRadius: 'var(--radius-md)', background: 'var(--red-50)', border: '1px solid var(--red-200)' }}>
                  <I n="alert" s={15} style={{ color: 'var(--red-600)', flexShrink: 0 }} />
                  <span style={{ flex: '1 1 240px', fontSize: 12.5, color: 'var(--text-body)' }}>
                    Cet écart n’est pas encore déclaré en non-conformité — l’audit ne pourra pas être
                    clôturé tant qu’il ne l’est pas. La NC reprendra l’intitulé du point{it.article ? ` et son article §${it.article}` : ''}.
                  </span>
                  <Bouton variante="danger" taille="sm" onClick={() => void run(`nc-${it.id}`, () => smq(`ged/audits/${a.documentId}/nc`, { itemId: it.id }, 'Écart déclaré en non-conformité.'))} disabled={estOccupe()}>
                    {estOccupe(`nc-${it.id}`) ? 'Déclaration…' : 'Déclarer la non-conformité'}
                  </Bouton>
                </div>
              )}
              {/* Le non-RQ voit l'exigence sans le bouton : elle le concerne
                  (il constate), la déclaration ne lui appartient pas. */}
              {it.verdict === 'ecart' && !it.ncRef && !isRQ && (
                <span style={{ flexBasis: '100%', fontSize: 12.5, color: 'var(--red-600)', paddingInlineStart: 4 }}>
                  À déclarer en non-conformité par le responsable qualité — la clôture de l’audit en dépend.
                </span>
              )}
              {/* Lot 7 — les PREUVES du point : ce qui a été regardé et les
                  documents vus. C'est ce qui rend le verdict opposable. */}
              {a.statut === 'realise' && canWork && (
                <PreuvesItem key={`p-${it.id}`} auditId={a.documentId} it={it} smq={smq} />
              )}
              {a.statut !== 'realise' && (it.preuves || it.documentsVus) && (
                <div style={{ flexBasis: '100%', fontSize: 12.5, color: 'var(--text-muted)', paddingInlineStart: 4 }}>
                  {it.preuves && <div><strong>Preuves :</strong> {it.preuves}</div>}
                  {it.documentsVus && <div><strong>Documents vus :</strong> {it.documentsVus}</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {onglet === 'constats' && (
      <div>
        {constats.length === 0 && (
          <EmptyState titre="Aucun constat enregistré."
            indice={a.statut === 'planifie' ? 'Marquez l’audit « réalisé » pour saisir les constats.' : undefined} />
        )}
        {constats.map((c) => (
          <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px dashed var(--border-subtle)', flexWrap: 'wrap' }}>
            {pill(vocabMeta(CONSTAT_TYPES, c.type))}
            <span style={{ flex: '1 1 260px', fontSize: 13, color: 'var(--text-body)' }}>{c.description}</span>
            {c.type === 'ecart' && (c.ncRef ? (
              enPanneau
                ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: 'var(--color-primary-active)' }}>{c.ncRef}</span>
                : <Bouton variante="primaire" taille="sm" onClick={() => gotoNC(c.ncRef!)} style={{ fontFamily: 'var(--font-mono)' }}>{c.ncRef} →</Bouton>
            ) : isRQ ? (
              <Bouton variante="danger" taille="sm" onClick={() => void run(`nc-${c.id}`, () => smq(`ged/audits/${a.documentId}/nc`, { constatId: c.id }, 'Écart déclaré en non-conformité.'))} disabled={estOccupe()}>
                {estOccupe(`nc-${c.id}`) ? 'Déclaration…' : 'Déclarer en NC →'}
              </Bouton>
            ) : null)}
          </div>
        ))}
        {a.statut === 'realise' && canWork && (
          <div style={{ marginTop: 10 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ display: 'inline-flex', gap: 5 }}>
                {Object.entries(CONSTAT_TYPES).map(([k, v]) => (
                  <button key={k} onClick={() => setCt({ ...ct, type: k })} aria-pressed={ct.type === k}
                    title={k === 'ecart' ? AIDE_ECART : undefined}
                    style={{ fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '5px 11px', cursor: 'pointer', border: `1.5px solid ${ct.type === k ? vocabMeta(CONSTAT_TYPES, k).fg : 'var(--border-default)'}`, background: ct.type === k ? vocabMeta(CONSTAT_TYPES, k).bg : 'var(--surface-card)', color: ct.type === k ? vocabMeta(CONSTAT_TYPES, k).fg : 'var(--text-muted)' }}>{v.label}</button>
                ))}
              </span>
              <input value={ct.description} onChange={(e) => { setCt({ ...ct, description: e.target.value }); if (erreurCt) setErreurCt(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && ct.description.trim()) { e.preventDefault(); void run('ct', async () => { const ok = await smq(`ged/audits/${a.documentId}/constat`, ct, 'Constat ajouté.'); if (ok) setCt({ type: 'ecart', description: '' }); }); } }}
                placeholder="Description du constat…" aria-invalid={!!erreurCt}
                style={{ ...inputStyle, height: 34, fontSize: 12.5, flex: '1 1 220px', borderColor: erreurCt ? 'var(--red-600)' : undefined }} />
              <Bouton variante="primaire" taille="sm" onClick={() => void run('ct', async () => {
                if (!ct.description.trim()) { setErreurCt('Décrivez le constat avant de l’ajouter.'); return; }
                setErreurCt('');
                const ok = await smq(`ged/audits/${a.documentId}/constat`, ct, 'Constat ajouté.');
                if (ok) setCt({ type: 'ecart', description: '' });
              })} disabled={estOccupe()}>
                <I n="plus" s={13} />{estOccupe('ct') ? 'Ajout…' : 'Ajouter le constat'}
              </Bouton>
            </div>
            <FieldError>{erreurCt}</FieldError>
          </div>
        )}
      </div>
      )}

      {onglet === 'liens' && <Liens type="audit" refCourante={a.ref} />}

      {/* PLUS D'ONGLET « JOURNAL » (RQ, 24/08/2026) — le bouton dédié, présent
         sur tous les registres, ouvre le même journal en fenêtre. Deux chemins
         pour la même chose obligeaient à savoir lequel prendre, et à les tenir
         tous les deux. Le journal n'est pas une rubrique du dossier : c'est sa
         preuve, elle s'ouvre par-dessus. */}

      </PanneauDossier>

      {/* Le compteur d'écarts non déclarés se lit depuis n'importe quel onglet :
          c'est lui qui explique pourquoi la clôture reste grisée. */}
      {canWork && (
        <BarreAction>
          {a.statut === 'planifie' && (
            <Bouton variante="primaire" taille="sm" onClick={() => void run('realise', () => smq(`ged/audits/${a.documentId}/status`, { statut: 'realise' }, 'Audit marqué réalisé — la grille est ouverte à la saisie.'))} disabled={estOccupe()}>
              {estOccupe('realise') ? 'En cours…' : 'Marquer réalisé — saisir les constats'}
            </Bouton>
          )}
          {/* Le bandeau NOMME ce qui bloque et le rend cliquable, au lieu de
              griser un bouton en laissant deviner pourquoi. Même composant, même
              règle et même dérogation motivée que la NC et la réclamation. */}
          {a.statut === 'realise' && (
            <BlocageCloture
              objet="l’audit"
              refDossier={a.ref}
              estRQ={isRQ}
              blocages={blocagesCloture}
              label="Clôturer l’audit"
              question={`Clôturer ${a.ref} ? Le rapport sera scellé et le statut ne pourra plus redescendre.`}
              aide="Un audit ne se clôture pas tant qu’un écart n’est pas déclaré en non-conformité, ni tant que ces non-conformités restent ouvertes (ISO 15189 §8.8.2 → §8.7). Sinon le rapport est soldé et les écarts ne le sont pas."
              indiceNonRQ="Déclarez les écarts en NC et faites-les traiter, ou demandez une dérogation au responsable qualité."
              onCloturer={(motifDerogation, responsable) => smq(
                `ged/audits/${a.documentId}/status`, { statut: 'cloture', motifDerogation, autorisation: responsable }, 'Audit clôturé.')} />
          )}
          {a.statut === 'cloture' && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700, color: 'var(--green-600)' }}><I n="check" s={14} />Audit clôturé</span>}
          {a.statut === 'annule' && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}><I n="alert" s={14} />Audit annulé — retiré du programme. Le motif est au journal.</span>}
          {/* Le rattrapage ne vaut que pour un audit JAMAIS TENU : dès qu'il est
              réalisé, ses constats sont acquis et la voie est la clôture. */}
          {isRQ && a.statut === 'planifie' && <AnnulerAudit a={a} />}
        </BarreAction>
      )}
    </div>
  );
}

/** Recueil des preuves d'un point de grille (lot 7) — saisie locale,
 *  enregistrée d'un geste. Un champ par nature : les PREUVES (ce qui a été
 *  regardé, mesuré, observé) et les DOCUMENTS VUS (codes et enregistrements),
 *  parce qu'un évaluateur les relit séparément. */
function PreuvesItem({ auditId, it, smq }: {
  auditId: string; it: GedGrilleItem;
  smq: (path: string, body?: unknown, okToast?: string) => Promise<boolean>;
}) {
  const [ouvert, setOuvert] = useState(!!(it.preuves || it.documentsVus));
  const [preuves, setPreuves] = useState(it.preuves || '');
  const [docs, setDocs] = useState(it.documentsVus || '');
  const [envoi, setEnvoi] = useState(false);
  const modifie = preuves !== (it.preuves || '') || docs !== (it.documentsVus || '');
  if (!ouvert) {
    return (
      <Bouton taille="sm" onClick={() => setOuvert(true)} title="Consigner les preuves et les documents vus pour ce point">
        <I n="pen" s={12} />Preuves
      </Bouton>
    );
  }
  return (
    <div style={{ flexBasis: '100%', display: 'grid', gap: 6, padding: '4px 0 2px', gridTemplateColumns: '1fr 1fr auto', alignItems: 'end' }}>
      <label style={{ display: 'grid', gap: 3, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>
        Preuves — ce qui a été regardé
        <textarea value={preuves} onChange={(e) => setPreuves(e.target.value)} rows={2}
          placeholder="Observations, mesures, personnes interrogées…"
          style={{ ...inputStyle, height: 'auto', padding: '7px 10px', fontSize: 12.5, fontWeight: 400, resize: 'vertical', fontFamily: 'var(--font-sans)', textTransform: 'none', letterSpacing: 'normal' }} />
      </label>
      <label style={{ display: 'grid', gap: 3, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>
        Documents et enregistrements vus
        <textarea value={docs} onChange={(e) => setDocs(e.target.value)} rows={2}
          placeholder="PR-QUAL-001 v3, fiche de vie EQ02, NC-2026-014…"
          style={{ ...inputStyle, height: 'auto', padding: '7px 10px', fontSize: 12.5, fontWeight: 400, resize: 'vertical', fontFamily: 'var(--font-sans)', textTransform: 'none', letterSpacing: 'normal' }} />
      </label>
      <Bouton variante="primaire" taille="sm" disabled={!modifie || envoi} onClick={async () => {
          setEnvoi(true);
          try { await smq(`ged/audits/${auditId}/grille`, { itemId: it.id, preuves, documentsVus: docs }, 'Preuves consignées.'); }
          finally { setEnvoi(false); }
        }}>
        {envoi ? '…' : 'Consigner'}
      </Bouton>
    </div>
  );
}

/**
 * Les PERSONNES AUDITÉES (12/08/2026) — §8.8.2.
 *
 * Champ libre : on rencontre en audit des gens qui n'ont pas de compte dans
 * l'application (stagiaire, préleveur externe, prestataire). Les contraindre à
 * figurer à l'organigramme aurait eu un seul effet — qu'on ne les écrive nulle
 * part. Se remplit tant que l'audit n'est pas clôturé ; ensuite, c'est une
 * pièce du rapport, et l'écran le dit au lieu de proposer un champ mort.
 */
function PersonnesAuditees({ a, peutEcrire }: { a: GedAudit; peutEcrire: boolean }) {
  const { smq } = useGed();
  const [valeur, setValeur] = useState(a.audites || '');
  const [envoi, setEnvoi] = useState(false);
  // La fiche se recharge après enregistrement : sans cette resynchronisation,
  // le champ garderait la saisie locale et masquerait ce qui est réellement
  // enregistré (y compris si le moteur a tronqué à 1 000 caractères).
  /* ⚠️ AJUSTÉ PENDANT LE RENDU, plus dans un effet (02/09/2026). L'effet
     rendait une première fois avec l'ANCIENNE valeur, puis re-rendait avec la
     nouvelle : sur un champ de saisie, cette passe intermédiaire est visible.
     Le motif est celui que React documente pour « ajuster un état quand une
     entrée change » — mémoriser la précédente, comparer, corriger sur place. */
  const [auditesPrec, setAuditesPrec] = useState(a.audites || '');
  if ((a.audites || '') !== auditesPrec) {
    setAuditesPrec(a.audites || '');
    setValeur(a.audites || '');
  }
  const modifie = valeur.trim() !== (a.audites || '').trim();

  if (!peutEcrire) {
    return (
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
        <strong style={{ color: 'var(--text-body)' }}>Personnes auditées :</strong>{' '}
        {a.audites ? <span style={{ whiteSpace: 'pre-line' }}>{a.audites}</span>
          : <em>non renseignées{a.statut === 'cloture' ? ' — l’audit est clôturé' : ''}</em>}
      </div>
    );
  }
  return (
    <label style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>
        Personnes auditées <span style={{ textTransform: 'none', letterSpacing: 'normal', fontWeight: 400 }}>
          — qui a été rencontré, à quel poste ; les personnes sans compte s’écrivent en toutes lettres
        </span>
      </span>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <textarea value={valeur} onChange={(e) => setValeur(e.target.value)} rows={2} maxLength={1000}
          placeholder="Ex. : Manel Haddada (accueil), Dr Ahmed Hmidet (biologie), Sami Trabelsi — prestataire maintenance EQ02"
          style={{ ...inputStyle, flex: 1, height: 'auto', padding: '7px 10px', fontSize: 12.5, resize: 'vertical' }} />
        <Bouton variante="primaire" taille="sm" disabled={!modifie || envoi} onClick={async () => {
            setEnvoi(true);
            try { await smq(`ged/audits/${a.documentId}/audites`, { audites: valeur.trim() }, 'Personnes auditées enregistrées.'); }
            finally { setEnvoi(false); }
          }}>
          {envoi ? '…' : 'Enregistrer'}
        </Bouton>
      </div>
    </label>
  );
}

function Grids() {
  const { settings, saveSettings, audits } = useGed();
  const [grids, setGrids] = useState(settings?.audGrids || []);
  const [newName, setNewName] = useState('');
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  // Resynchronisation pendant le rendu : les grilles viennent du paramétrage,
  // qui peut être rechargé par ailleurs. On ne réécrit que si la référence a
  // changé — sinon le retour de notre propre enregistrement écraserait une
  // saisie en cours.
  const [vues, setVues] = useState(settings?.audGrids);
  if (vues !== settings?.audGrids) {
    setVues(settings?.audGrids);
    setGrids(settings?.audGrids || []);
  }

  // Les versions lues avec les grilles affichées : un autre RQ qui a modifié
  // les modèles entre-temps fait refuser l'enregistrement au lieu d'être écrasé.
  const save = (next: typeof grids) => { setGrids(next); void saveSettings({ audGrids: next } as never, settings?.versions); };

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <input value={newName} onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim()) { e.preventDefault(); save([...grids, { id: `g_${Date.now()}`, nom: newName.trim(), items: [] }]); setNewName(''); } }}
          placeholder="Nouveau modèle de grille…" style={{ ...inputStyle, maxWidth: 320 }} />
        <Bouton variante="primaire" taille="sm" onClick={() => { if (!newName.trim()) return; save([...grids, { id: `g_${Date.now()}`, nom: newName.trim(), items: [] }]); setNewName(''); }} disabled={!newName.trim()}><I n="plus" s={13} />Créer</Bouton>
      </div>
      {grids.length === 0 && (
        <div style={card}>
          <EmptyState titre="Aucun modèle de grille." indice="Créez-en un ci-dessus : il pourra être rattaché à un audit lors de sa planification." />
        </div>
      )}
      {grids.map((g, gi) => {
        // Une grille déjà rattachée à un audit a été recopiée dans cet audit :
        // la supprimer ici ne touche pas les audits, il faut le dire.
        const usages = audits.filter((a) => a.grilleId === g.id).length;
        return (
          <div key={g.id} style={{ ...card, padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--text-strong)', flex: 1 }}>{g.nom}</h3>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{g.items.length} point(s){usages > 0 ? ` · utilisée par ${usages} audit(s)` : ''}</span>
              <ConfirmButton
                label="" icon="trash" variant="danger" titre={`Supprimer le modèle « ${g.nom} »`}
                question={`Supprimer le modèle « ${g.nom} » et ses ${g.items.length} point(s) de contrôle ? Les audits déjà planifiés conservent leur copie de la grille.`}
                confirmer="Supprimer"
                onConfirm={() => save(grids.filter((_, j) => j !== gi))} />
            </div>
            {g.items.map((it, ii) => (
              <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px dashed var(--border-subtle)' }}>
                <span style={{ flex: 1, fontSize: 13, color: 'var(--text-body)' }}>
                  {it.article && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, fontWeight: 700, color: 'var(--color-primary-active)', marginInlineEnd: 7 }}>§{it.article}</span>}
                  {it.label}
                  {it.attendu && (
                    <span title={`Attendu : ${it.attendu}`} style={{ marginInlineStart: 6, cursor: 'help', color: 'var(--text-muted)' }}>
                      <I n="info" s={12} style={{ display: 'inline-block', verticalAlign: '-2px' }} />
                    </span>
                  )}
                </span>
                <ConfirmButton
                  label="" icon="x" variant="danger" titre={`Retirer « ${it.label} » de la grille`}
                  question={`Retirer « ${it.label} » de la grille ?`}
                  confirmer="Retirer"
                  onConfirm={() => save(grids.map((x, j) => j !== gi ? x : { ...x, items: x.items.filter((_, k) => k !== ii) }))} />
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              {/* Lot 7 — un point porte son libellé, l'article ISO qu'il éprouve
                  et l'ATTENDU (ce que l'auditeur cherchera) : les trois se
                  saisissent d'un geste, seuls le libellé est obligatoire. */}
              <input value={drafts[g.id] || ''} onChange={(e) => setDrafts({ ...drafts, [g.id]: e.target.value })}
                placeholder="Nouveau point de contrôle…" style={{ ...inputStyle, height: 34, fontSize: 12.5, flex: '2 1 240px' }} />
              <input value={drafts[`${g.id}:art`] || ''} onChange={(e) => setDrafts({ ...drafts, [`${g.id}:art`]: e.target.value })}
                placeholder="Article (7.3.7.2)" style={{ ...inputStyle, height: 34, fontSize: 12.5, flex: '0 1 130px', fontFamily: 'var(--font-mono)' }} />
              <input value={drafts[`${g.id}:att`] || ''} onChange={(e) => setDrafts({ ...drafts, [`${g.id}:att`]: e.target.value })}
                placeholder="Attendu — ce que l’auditeur cherche" style={{ ...inputStyle, height: 34, fontSize: 12.5, flex: '2 1 220px' }} />
              <Bouton taille="sm" onClick={() => {
                const v = (drafts[g.id] || '').trim(); if (!v) return;
                const article = (drafts[`${g.id}:art`] || '').trim();
                const attendu = (drafts[`${g.id}:att`] || '').trim();
                save(grids.map((x, j) => j !== gi ? x : { ...x, items: [...x.items, { id: `gi_${Date.now()}`, label: v, ...(article ? { article } : {}), ...(attendu ? { attendu } : {}) }] }));
                setDrafts({ ...drafts, [g.id]: '', [`${g.id}:art`]: '', [`${g.id}:att`]: '' });
              }} disabled={!(drafts[g.id] || '').trim()}>Ajouter</Bouton>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * AIDE À LA CONDUITE DE L'AUDIT — dépliable, sous le point.
 *
 * Demandé par la RQ le 14/08/2026 : « faciliter au plus à un auditeur non
 * expérimenté de réaliser l'audit ».
 *
 * ── Pourquoi dépliable, et pourquoi pas une infobulle ──────────────────────
 *
 * L'attendu existait déjà, mais en `title=` : il fallait SAVOIR qu'il était là,
 * viser une icône de 13 px, et il disparaissait dès qu'on bougeait la souris —
 * donc impossible à lire en même temps qu'on écrit sa note. Une aide qu'on ne
 * peut pas garder sous les yeux pendant l'entretien n'aide pas.
 *
 * Dépliée et non ouverte d'emblée : un auditeur chevronné n'a pas à faire
 * défiler trois écrans de conseils pour atteindre le point suivant. Le pli est
 * la seule façon de servir les deux.
 */
/**
 * Retrouver l'aide d'un point dans le FONDS, quand la copie ne la porte pas.
 *
 * La grille d'un audit est figée à sa planification — décision du lot 7, et
 * elle est juste : ce qui a été éprouvé ne doit pas changer sous les pieds de
 * l'auditeur, ni après coup dans le dossier. Mais l'aide à la conduite n'est
 * pas un enregistrement : c'est un mode d'emploi. La figer aurait deux effets
 * fâcheux — les audits déjà planifiés n'en verraient jamais, et une aide
 * corrigée dans le fonds ne profiterait pas à l'audit en cours.
 *
 * On lit donc l'aide en direct dans le fonds, et on la superpose SANS jamais
 * toucher aux champs de l'enregistrement (verdict, preuves recueillies,
 * commentaire) : la copie de l'audit reste maîtresse de tout ce qui a été
 * constaté. L'appariement se fait sur l'identifiant du point, et à défaut sur
 * le libellé — un point recopié à l'identique porte le même texte.
 */
function aide(settings: GedSettings | null | undefined, a: GedAudit, it: GedGrilleItem): GedGrilleItem {
  if (it.question || it.preuvesADemander?.length || it.piege) return it;
  const fonds = settings?.audGrids?.find((g) => g.id === a.grille?.id || g.nom === a.grille?.nom);
  const ref = fonds?.items.find((x) => x.id === it.id) || fonds?.items.find((x) => x.label === it.label);
  if (!ref) return it;
  return {
    ...it,
    ...(ref.attendu && !it.attendu ? { attendu: ref.attendu } : {}),
    ...(ref.question ? { question: ref.question } : {}),
    ...(ref.preuvesADemander ? { preuvesADemander: ref.preuvesADemander } : {}),
    ...(ref.observer ? { observer: ref.observer } : {}),
    ...(ref.ecarts ? { ecarts: ref.ecarts } : {}),
    ...(ref.piege ? { piege: ref.piege } : {}),
  };
}

function AideAuditeur({ it }: { it: GedGrilleItem }) {
  const [ouvert, setOuvert] = React.useState(false);
  const aDeQuoi = it.question || it.preuvesADemander?.length || it.observer || it.ecarts?.length || it.piege;
  if (!aDeQuoi) return null;
  return (
    <>
      <button type="button" onClick={() => setOuvert((v) => !v)} aria-expanded={ouvert}
        title="Comment conduire ce point : la question à poser, ce qu'il faut se faire montrer, les écarts typiques."
        style={{ marginInlineStart: 8, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>
        {ouvert ? 'Masquer l’aide' : 'Comment auditer ce point ?'}
      </button>
      {ouvert && (
        <div style={{ marginTop: 8, display: 'grid', gap: 8, background: 'var(--gray-25)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '11px 13px', fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-body)' }}>
          {it.question && (
            <div>
              <div style={ptTitre}>La question à poser</div>
              {/* En italique et entre guillemets : c'est une formulation à dire
                  telle quelle, pas un résumé à reformuler. Une question
                  reformulée à la volée devient vite suggestive. */}
              <div style={{ fontStyle: 'italic' }}>{it.question}</div>
            </div>
          )}
          {!!it.preuvesADemander?.length && (
            <div>
              <div style={ptTitre}>Ce qu’il faut se faire montrer</div>
              <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                {it.preuvesADemander.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
          )}
          {it.observer && (
            <div>
              <div style={ptTitre}>Ce qui se voit et ne se lit pas</div>
              <div>{it.observer}</div>
            </div>
          )}
          {!!it.ecarts?.length && (
            <div>
              <div style={{ ...ptTitre, color: 'var(--red-600)' }}>Écarts typiques sur ce point</div>
              <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                {it.ecarts.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
          {it.piege && (
            <div style={{ background: 'var(--amber-50)', borderRadius: 'var(--radius-sm)', padding: '8px 10px', color: 'var(--amber-700)' }}>
              <strong>Le piège :</strong> {it.piege}
            </div>
          )}
          <div style={{ fontSize: 12.5, color: 'var(--text-faint)', lineHeight: 1.45 }}>
            Ces éléments aident à conduire l’entretien — ils ne rendent aucun verdict.
            C’est vous qui avez vu le terrain.
          </div>
        </div>
      )}
    </>
  );
}

const ptTitre: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 2,
};

/* L8 (11/10/2026) : tri du socle (`useTri`) ; l'ordre métier (en retard d'abord,
   puis date planifiée) reste l'ordre par défaut, dit par `MentionTri`. */
const VALEURS_TRI_AUDITS: Record<string, (a: GedAudit) => string> = {
  ref: (a) => a.ref,
  datePlanifiee: (a) => a.datePlanifiee || '',
  statut: (a) => a.statut,
};

export function Audits() {
  const { audits, selectedAudit, select, session, settings, loading, booted, smqFiltre } = useGed();
  const [tab, setTab] = useState<'prog' | 'grids' | 'acces'>('prog');
  // Un sous-onglet = une arrivée : on remonte (règle du 24/08/2026, cf. le crochet).
  useRemonteEnHaut(tab);
  const [showNew, setShowNew] = useState(false);
  const [brouillon, setBrouillon] = useState(false);
  const [q, setQ] = useState('');
  // Filtre demandé par le compteur qui nous a amenés ici — lu au montage
  // seulement (`go` l'efface, donc jamais de filtre hérité d'une visite passée).
  const [filtre, setFiltre] = useState(smqFiltre ?? 'tous');
  const isRQ = session.roles.includes('rq');
  const sel = audits.find((a) => a.documentId === selectedAudit) || null;
  const enChargement = !booted || (loading && audits.length === 0);

  const planifies = audits.filter((a) => a.statut === 'planifie');
  const retards = audits.filter(auditEnRetard);
  const aTraiter = audits.filter((a) => a.statut === 'realise');
  const clotures = audits.filter((a) => a.statut === 'cloture');

  const listeFiltree = useMemo(() => {
    const base = filtre === 'planifies' ? planifies
      : filtre === 'retards' ? retards
        : filtre === 'realises' ? aTraiter
          : filtre === 'clotures' ? clotures
            : audits;
    const qq = q.trim().toLowerCase();
    const cherche = qq ? base.filter((a) => `${a.ref} ${a.intitule} ${a.perimetre} ${userName(settings, a.auditeur)}`.toLowerCase().includes(qq)) : base;
    // Les dates dépassées d'abord, puis par date planifiée.
    return [...cherche].sort((x, y) => {
      const rx = auditEnRetard(x) ? 0 : 1, ry = auditEnRetard(y) ? 0 : 1;
      if (rx !== ry) return rx - ry;
      return (x.datePlanifiee || '').localeCompare(y.datePlanifiee || '');
    });
  }, [audits, filtre, q, settings, planifies, retards, aTraiter, clotures]);
  const { tri, basculer: onTri, trier } = useTri<GedAudit>(null, VALEURS_TRI_AUDITS);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 18 }}>  {/* `minmax(0, 1fr)` : un élément de grille refuse par défaut de
      descendre sous sa largeur de contenu, si bien qu'un registre large
      étirait le bandeau de titre et la barre de filtres hors du cadre
      (constaté sur les NC le 19/08/2026). Le `0` autorise la colonne à
      se réduire ; ce qui est large défile dans son propre conteneur. */}
      <TitrePage eyebrow={<>Évaluation · <RefISO code="8.8.3" /><Norme art="8.8.3" /></>} titre="Audits internes"
        actions={isRQ && (
          <BoutonCreer onClick={() => { setShowNew(true); setTab('prog'); select('selectedAudit', null); }}>
            Planifier un audit{!showNew && brouillon ? ' (brouillon conservé)' : ''}
          </BoutonCreer>
        )} />

      <Segment ariaLabel="Section" valeur={tab} onChange={setTab}
        options={[
          { valeur: 'prog' as const, libelle: 'Programme d’audit' },
          { valeur: 'grids' as const, libelle: 'Modèles de grille' },
          /* ⚠️ « Accès auditeurs » (MAN-9) est RÉSERVÉ AU RQ. C'est la seule porte qui s'ouvre vers
             l'extérieur : l'onglet n'existe pas pour qui ne peut pas s'en servir, plutôt que
             d'exister et de refuser. */
          ...(isRQ ? [{ valeur: 'acces' as const, libelle: 'Accès auditeurs' }] : []),
        ]} />

      {tab === 'acces' ? <AccesAudits /> : tab === 'grids' ? <Grids /> : (
        <>
          {isRQ && <PlanForm visible={showNew} onDone={() => setShowNew(false)} onDirty={setBrouillon} />}
          <div className="ged-2col-wide ged-colonnes-defilantes" style={{ display: 'grid', gridTemplateColumns: '360px minmax(0, 1fr)', gap: 18, alignItems: 'start' }}>
            <div>
              <FilterBar
                q={q} onQ={setQ} placeholder="Rechercher un audit…"
                chips={[
                  { k: 'tous', label: 'Tous', n: audits.length },
                  { k: 'planifies', label: 'Planifiés', n: planifies.length },
                  { k: 'retards', label: 'Date dépassée', n: retards.length },
                  { k: 'realises', label: 'À clôturer', n: aTraiter.length },
                  { k: 'clotures', label: 'Clôturés', n: clotures.length },
                ]}
                actif={filtre} onChip={setFiltre}
                total={audits.length} affiches={listeFiltree.length}
                onReset={() => { setQ(''); setFiltre('tous'); }} />
              <div style={{ display: 'flex', gap: 14, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <MentionTri>en retard d’abord, puis par date planifiée — le prochain dû est en tête.</MentionTri>
                <span style={{ flex: 1 }} />
                <BoutonsTri label="Trier le programme d’audit" tri={tri} onTri={onTri}
                  options={[{ cle: 'ref', libelle: 'Référence' }, { cle: 'datePlanifiee', libelle: 'Date planifiée' }, { cle: 'statut', libelle: 'Statut' }]} />
              </div>
              <div style={{ ...card, overflow: 'hidden' }}>
                {enChargement ? <Skeleton rows={5} height={64} /> : trier(listeFiltree).map((a) => {
                  const ecarts = (a.constats || []).filter((c) => c.type === 'ecart').length + ((a.grille?.items || []).filter((i) => i.verdict === 'ecart').length);
                  const tard = auditEnRetard(a);
                  return (
                    <LigneLien key={a.documentId} href={cheminDe('audits', a.ref)}
                      title={`Ouvrir ${a.ref}`}
                      onOuvrir={() => { select('selectedAudit', a.documentId); setShowNew(false); }}
                      style={{ width: '100%', textAlign: 'start', background: selectedAudit === a.documentId ? 'var(--cyan-50)' : 'none', borderTop: '1px solid var(--border-subtle)', padding: '12px 14px', fontFamily: 'var(--font-sans)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{a.ref}</span>
                        {pill(vocabMeta(AUDIT_STATES, a.statut))}
                        {tard && <LateBadge jours={joursRestants(a.datePlanifiee)} libelleRetard="Date dépassée" />}
                        {ecarts > 0 && <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--red-600)' }}>{ecarts} écart(s)</span>}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)', marginTop: 5, lineHeight: 1.35 }}>{a.intitule}</div>
                      <div style={{ fontSize: 12.5, color: tard ? 'var(--red-600)' : 'var(--text-muted)', marginTop: 3, fontWeight: tard ? 600 : 400 }}>{fmtDate(a.datePlanifiee)} · {a.perimetre || '—'}</div>
                    </LigneLien>
                  );
                })}
                {!enChargement && listeFiltree.length === 0 && (
                  <EmptyState
                    titre={audits.length === 0 ? 'Aucun audit au programme.' : 'Aucun audit ne correspond à ces critères.'}
                    indice={audits.length === 0 ? 'Planifiez un audit pour ouvrir le programme annuel.' : 'Élargissez la recherche ou revenez à « Tous ».'}
                    action={audits.length > 0
                      ? <Bouton taille="sm" onClick={() => { setQ(''); setFiltre('tous'); }}>Réinitialiser les filtres</Bouton>
                      : undefined} />
                )}
              </div>
            </div>
            {/* `key` : changer d'audit remonte la fiche — onglet et constat en
                cours de saisie repartent de zéro. */}
            {sel ? <Detail key={sel.documentId} a={sel} /> : (
              <div style={card}>
                <EmptyState titre="Dossier d’audit" indice="Sélectionnez un audit du programme pour le conduire." />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
